import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import Layout from '../../../components/admin/Layout';
import IconPicker from '../../../components/admin/IconPicker';
import ToggleSwitch from '../../../components/common/ToggleSwitch';
import axios from '../../../utils/axios';
import { LanguageContext } from '../../../context/LanguageContext';
import { adminTranslations } from '../../../translations/admin';

const EditCategory = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { language } = useContext(LanguageContext);
    const isRTL = language === 'ar';
    
    const [formData, setFormData] = useState({
        name: '',
        name_ar: '',
        slug: '',
        icon: '',
        is_active: true,
        sort_order: 0
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    const t = useCallback((key) => {
        return adminTranslations[language]?.[key] || key;
    }, [language]);

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/categories/${id}`);
                const category = response.data.data;
                setFormData({
                    name: category.name,
                    name_ar: category.name_ar,
                    slug: category.slug,
                    icon: category.icon,
                    sort_order: category.sort_order,
                    is_active: category.is_active
                });
                setLoading(false);
            } catch (error) {
                console.error('Error fetching category:', error);
                toast.error(t('errorLoadingCategory'));
                setLoading(false);
            }
        };

        fetchCategory();
    }, [id, t]);

    const handleChange = useCallback((e) => {
        const { name, value, type } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? e.target.checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    }, [errors]);

    const handleIconChange = useCallback((iconName) => {
        setFormData(prev => ({ ...prev, icon: iconName }));
        if (errors.icon) {
            setErrors(prev => ({ ...prev, icon: null }));
        }
    }, [errors]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('_method', 'PUT');
            formDataToSend.append('name', formData.name);
            formDataToSend.append('name_ar', formData.name_ar);
            formDataToSend.append('slug', formData.slug);
            formDataToSend.append('icon', formData.icon);
            formDataToSend.append('sort_order', formData.sort_order);
            formDataToSend.append('is_active', formData.is_active ? 1 : 0);

            await axios.post(`/categories/${id}`, formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            toast.success(t('categoryUpdated'));
            navigate('/admin/categories');
        } catch (err) {
            if (err.response?.data?.errors) {
                setErrors(err.response.data.errors);
            } else {
                toast.error(t('errorUpdatingCategory'));
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={`max-w-6xl mx-auto px-4 py-8 ${isRTL ? 'rtl' : ''}`}>
                <button
                    onClick={() => navigate('/admin/categories')}
                    className={`inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 group
                        ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                    <FiArrowLeft 
                        className={`h-4 w-4 transition-transform group-hover:translate-x-[-4px]
                        ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} 
                    />
                    {t('backToCategories')}
                </button>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                    <form onSubmit={handleSubmit}>
                        {/* Header Section */}
                        <div className="px-6 py-4 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-800">
                                    {t('editCategory')}
                                </h2>
                                <ToggleSwitch
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    name="is_active"
                                    label={t('status')}
                                />
                            </div>
                        </div>

                        <div className="p-6 space-y-8">
                            {/* Basic Information */}
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('nameEnglish')}
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                className={`w-full px-4 py-2.5 rounded-lg border ${
                                                    errors.name ? 'border-red-500' : 'border-gray-200'
                                                } bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 
                                                transition-all duration-200 text-gray-900 text-base`}
                                            />
                                            {errors.name && <p className="mt-1.5 text-sm text-red-500">{errors.name[0]}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('categorySlug')}
                                            </label>
                                            <input
                                                type="text"
                                                name="slug"
                                                value={formData.slug}
                                                onChange={handleChange}
                                                required
                                                className={`w-full px-4 py-2.5 rounded-lg border ${
                                                    errors.slug ? 'border-red-500' : 'border-gray-200'
                                                } bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 
                                                transition-all duration-200 text-gray-900 text-base`}
                                            />
                                            {errors.slug && <p className="mt-1.5 text-sm text-red-500">{errors.slug[0]}</p>}
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('nameArabic')}
                                            </label>
                                            <input
                                                type="text"
                                                name="name_ar"
                                                value={formData.name_ar}
                                                onChange={handleChange}
                                                dir="rtl"
                                                required
                                                className={`w-full px-4 py-2.5 rounded-lg border ${
                                                    errors.name_ar ? 'border-red-500' : 'border-gray-200'
                                                } bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 
                                                transition-all duration-200 text-gray-900 text-base`}
                                            />
                                            {errors.name_ar && <p className="mt-1.5 text-sm text-red-500">{errors.name_ar[0]}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('sortOrder')}
                                            </label>
                                            <input
                                                type="number"
                                                name="sort_order"
                                                value={formData.sort_order}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-2.5 rounded-lg border ${
                                                    errors.sort_order ? 'border-red-500' : 'border-gray-200'
                                                } bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 
                                                transition-all duration-200 text-gray-900 text-base`}
                                            />
                                            {errors.sort_order && <p className="mt-1.5 text-sm text-red-500">{errors.sort_order[0]}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Media Section */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Icon Picker */}
                                <div className="md:col-span-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('categoryIcon')}
                                    </label>
                                    <div className={`${isRTL ? 'rtl' : ''} bg-gray-50 rounded-lg p-4`}>
                                        <IconPicker
                                            value={formData.icon}
                                            onChange={handleIconChange}
                                            error={errors.icon && errors.icon[0]}
                                        />
                                    </div>
                                    {errors.icon && <p className="mt-1.5 text-sm text-red-500">{errors.icon[0]}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-2xl">
                            <div className="flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={() => navigate('/admin/categories')}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                >
                                    <FiArrowLeft className={isRTL ? 'ml-2' : 'mr-2'} />
                                    {t('cancel')}
                                </button>

                                <button
                                    type="submit"
                                    disabled={saving}
                                    className={`inline-flex items-center px-6 py-3 bg-yellow-500 text-white rounded-lg
                                        hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 
                                        focus:ring-offset-2 transform transition-all duration-200
                                        ${saving ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md active:scale-95'}`}
                                >
                                    <FiSave className={isRTL ? 'ml-2' : 'mr-2'} />
                                    {saving ? t('saving') : t('save')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default EditCategory;
